/**
 * SearchBar Component
 * Search input with icon, optional search/clear buttons
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaSearch, FaTimes } from "react-icons/fa";
import "./SearchBar.scss";

export const SearchBar = ({
  value = "",
  onChange,
  onSearch,
  onClear,
  placeholder = "Search...",
  disabled = false,
  className = "",
  showSearchButton = false,
  showClearButton = false,
}) => {
  const [internalValue, setInternalValue] = useState(value);

  // Sync internal state when value prop changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = (newValue) => {
    setInternalValue(newValue);
    // If no search button, update immediately (instant filtering)
    if (!showSearchButton && onChange) {
      onChange(newValue);
    }
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(internalValue);
    }
    if (onChange) {
      onChange(internalValue);
    }
  };

  const handleClear = () => {
    setInternalValue("");
    if (onChange) {
      onChange("");
    }
    if (onClear) {
      onClear();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && showSearchButton) {
      handleSearch();
    }
  };

  return (
    <div
      className={`search-bar ${
        disabled ? "search-bar--disabled" : ""
      } ${className}`}
    >
      <input
        type="text"
        className="search-bar__input"
        placeholder={placeholder}
        value={showSearchButton ? internalValue : value}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {showClearButton && (value || internalValue) && (
        <button
          type="button"
          className="search-bar__button search-bar__button--clear"
          onClick={handleClear}
          disabled={disabled}
          title="Clear search"
        >
          <FaTimes />
        </button>
      )}
      {showSearchButton && (
        <button
          type="button"
          className="search-bar__button search-bar__button--search"
          onClick={handleSearch}
          disabled={disabled || !internalValue.trim()}
          title="Search"
        >
          <FaSearch />
        </button>
      )}
    </div>
  );
};

SearchBar.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showSearchButton: PropTypes.bool,
  showClearButton: PropTypes.bool,
};
